import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
// Lazy: the signed-in app is ~2.9 MB of JavaScript (746 KB gzipped) and a public page (/f/, /s/, /pi/, /survey/) never uses a byte of it. Importing it
// statically made a stranger filling in a form download the whole product first (WP-26.3). The public pages below stay static: they ARE the entry.
const App = lazy(() => import('./App'));
import { ToastProvider } from './shared/ToastContext';
import { ConfirmProvider } from './shared/ConfirmContext';
import { DrilldownProvider } from './shared/DrilldownContext';
import { ErrorBoundary } from './shared/ErrorBoundary';
import { readFormLink, readShareLink, readPublicIntake, readSurveyInvite } from './auth/authLinks';
import { PublicFormPage } from './live/PublicFormPage';
import { PublicSharePage } from './live/PublicSharePage';
import { PublicIntakePage } from './live/PublicIntakePage';
import { SurveyPage } from './live/SurveyPage';
import './index.css';

// A tokenised form link (`/f/<token>`, docs/tokenised-form-links-scoping-2026-09.md
// Phase 1) branches HERE rather than inside App.jsx, above its login gate.
// App.jsx's first act is to boot a session — getMe, the app list, org billing
// — and a form-link recipient has no session for any of that to be about, so
// mounting it at all would mean a burst of 401s and a login screen flashing
// behind the form. The token is the entire authority on this screen; there is
// deliberately no route from here into the signed-in app.
const formLinkToken = readFormLink();
// `/s/<token>` branches here for the same reason `/f/` does: a share-link
// recipient has no session for App.jsx's boot to be about, so mounting it
// would mean a burst of 401s and a login screen flashing behind the page.
const shareLinkToken = readShareLink();
// `/pi/<key>` is the public intake form, the third page that branches here.
const publicIntakeKey = readPublicIntake();
// `/survey/<token>` is a survey invite: the fourth, for the same reason.
const surveyToken = readSurveyInvite();

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      {formLinkToken ? (
        <PublicFormPage token={formLinkToken} />
      ) : shareLinkToken ? (
        <PublicSharePage token={shareLinkToken} />
      ) : publicIntakeKey ? (
        <PublicIntakePage intakeKey={publicIntakeKey} />
      ) : surveyToken ? (
        <SurveyPage token={surveyToken} />
      ) : (
        <ToastProvider>
          <ConfirmProvider>
            <DrilldownProvider>
              <Suspense fallback={<div className="min-h-screen bg-white dark:bg-neutral-950" aria-busy="true" />}>
                <App />
              </Suspense>
            </DrilldownProvider>
          </ConfirmProvider>
        </ToastProvider>
      )}
    </ErrorBoundary>
  </React.StrictMode>
);
